import React from "react";
import InputWithUnitSelect from "../../CoilConfigurator/components/InputWithUnitSelect";

type UnitOption = { value: string; label: string };

interface ValueWithTolerancesProps {
  id: string;
  label: string;
  value: string;
  plusTolerance: string;
  minusTolerance: string;
  onValueChange: (value: string) => void;
  onPlusChange: (value: string) => void;
  onMinusChange: (value: string) => void;
  /** When set, nominal uses unit select; otherwise a plain number/text input. */
  unit?: string;
  unitOptions?: UnitOption[];
  onUnitChange?: (unit: string) => void;
  valuePlaceholder?: string;
  plusPlaceholder?: string;
  minusPlaceholder?: string;
  error?: string;
  /** Appended after the nominal label when no unit select (e.g. "°"). */
  valueSuffix?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  type?: "text" | "number";
  step?: string | number;
  min?: string | number;
}

/**
 * Nominal value with optional +tol / −tol inputs. Maps to existing API tolerance fields.
 */
const ValueWithTolerances = ({
  id,
  label,
  value,
  plusTolerance,
  minusTolerance,
  onValueChange,
  onPlusChange,
  onMinusChange,
  unit,
  unitOptions,
  onUnitChange,
  valuePlaceholder,
  plusPlaceholder = "+tol",
  minusPlaceholder = "−tol",
  error,
  valueSuffix,
  inputMode = "decimal",
  type = "text",
  step,
  min,
}: ValueWithTolerancesProps) => {
  const useUnitSelect = Boolean(unitOptions && onUnitChange && unit != null);

  return (
    <div className="modal-field op-tol-triplet">
      <span className="op-tol-triplet__label" id={`${id}-label`}>
        {label}
      </span>
      <div className="op-tol-triplet__inputs" role="group" aria-labelledby={`${id}-label`}>
        <div className="op-tol-triplet__cell">
          <span>Nominal</span>
          {useUnitSelect ? (
            <InputWithUnitSelect
              id={id}
              name={id}
              value={value}
              unit={unit!}
              unitOptions={unitOptions!}
              onValueChange={onValueChange}
              onUnitChange={onUnitChange!}
            />
          ) : (
            <div className="op-tol-triplet__value-wrap">
              <input
                id={id}
                className="op-input"
                type={type}
                inputMode={inputMode}
                step={step}
                min={min}
                placeholder={valuePlaceholder}
                value={value}
                onChange={(event) => onValueChange(event.target.value)}
                aria-label={`${label} nominal`}
              />
              {valueSuffix ? <span className="op-tol-triplet__suffix">{valueSuffix}</span> : null}
            </div>
          )}
        </div>
        <div className="op-tol-triplet__cell">
          <span>+tol</span>
          <input
            id={`${id}-plus`}
            className="op-input"
            type="text"
            inputMode="decimal"
            placeholder={plusPlaceholder}
            value={plusTolerance}
            onChange={(event) => onPlusChange(event.target.value)}
            aria-label={`${label} plus tolerance`}
          />
        </div>
        <div className="op-tol-triplet__cell">
          <span>−tol</span>
          <input
            id={`${id}-minus`}
            className="op-input"
            type="text"
            inputMode="decimal"
            placeholder={minusPlaceholder}
            value={minusTolerance}
            onChange={(event) => onMinusChange(event.target.value)}
            aria-label={`${label} minus tolerance`}
          />
        </div>
      </div>
      {error ? (
        <p className="op-field-error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
};

export const formatWithTolerances = (
  nominal: string | null | undefined,
  plusTol: string | null | undefined,
  minusTol: string | null | undefined,
  unit?: string,
) => {
  const n = nominal?.trim();
  if (!n) return null;
  const unitPart = !unit?.trim()
    ? ""
    : /^[°%]/.test(unit.trim())
      ? unit.trim()
      : ` ${unit.trim()}`;
  const plus = plusTol?.trim();
  const minus = minusTol?.trim();
  if (!plus && !minus) return `${n}${unitPart}`;
  const plusPart = plus ? `+${plus}` : "";
  const minusPart = minus ? `−${minus}` : "";
  const tol = [plusPart, minusPart].filter(Boolean).join(" / ");
  return `${n}${unitPart} (${tol})`;
};

export default ValueWithTolerances;
